import React, { useEffect, useState } from "react";
import StatusBadge from "../../../../common/StatusBadge";
import ToastMessage from "../../../../common/ToastMessage";
import CustomDataGrid from "../../../../common/CustomDataGrid";
import useStatuses from "../../../../store/ReactQueries/statuses/useStatus";
import useAddStatus from "../../../../store/ReactQueries/statuses/useAddStatuses";
import useUpdateStatus from "../../../../store/ReactQueries/statuses/useUpdateStatus";
import useDeleteStatus from "../../../../store/ReactQueries/statuses/useDeleteStatus";
import { GridColDef } from "@mui/x-data-grid";
import { PAGESIZE } from "../../../../utils/constants";
import { FieldErrors } from "../../../../domain/helpers/Errors";
import { formatDate } from "../../../../common/GlobalFunctions";
import { validateSchema } from "../../../../common/ValidateSchema";
import { useLanguageStore } from "../../../../store/useLanguageStore";
import { useDarkModeStore } from "../../../../store/useDarkModeStore";
import { StatusInterface } from "../../../../models/TicketModule/Status";
import { statusSchema } from "../../../../domain/ValidationSchemas/Ticket/status";

export const Statuses = () => {
  const { language } = useLanguageStore();
  const { darkMode } = useDarkModeStore();

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGESIZE);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [form, setForm] = useState<StatusInterface>({
    id: "",
    title: "",
    badgeColor: '#8D97A0',
    order: 0,
    isDefault: false,
    isCompleted: false,
    isReadOnly: false,
    isReadyForReview: false,
  });

  const [modal, setModal] = useState<{
    add: boolean;
    edit: boolean;
    view: boolean;
  }>({
    add: false,
    edit: false,
    view: false,
  });

  let { data: statuses, isLoading, error: statusError, refetch } = useStatuses(page, pageSize);

  const addStatus = useAddStatus(page, pageSize);
  const updateStatus = useUpdateStatus(page, pageSize);
  const deleteStatus = useDeleteStatus(page, pageSize);

  useEffect(() => {
    if (statusError) ToastMessage.Failed(`Error: ${statusError.message}`);
  }, [statusError]);

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "Id",
      type: "string",
      editable: false,
    },
    {
      field: "title",
      headerName: "Title",
      type: "string",
      editable: true,
      width: 200,
      cellClassName: 'd-flex align-items-center',
      renderCell: ({ row }) => <StatusBadge name={row.title ? row.title : "No Status"} color={row.badgeColor || '#8D97A0'} width={"fit-content"} fontSize={12} />
    },
    {
      field: "isDefault",
      headerName: "Default",
      type: "boolean",
      editable: true,
      renderCell: ({ value }) => (
        <span style={{ color: darkMode ? "white" : "black" }}>
          {value ? `✓` : "✕"}
        </span>
      ),
    },
    {
      field: "isCompleted",
      headerName: "Completed",
      type: "boolean",
      editable: true,
      renderCell: ({ value }) => (
        <span style={{ color: darkMode ? "white" : "black" }}>
          {value ? `✓` : "✕"}
        </span>
      ),
    },
    {
      field: "isReadOnly",
      headerName: "Read-only",
      type: "boolean",
      editable: true,
      renderCell: ({ value }) => (
        <span style={{ color: darkMode ? "white" : "black" }}>
          {value ? `✓` : "✕"}
        </span>
      ),
    },
    {
      field: "isReadyForReview",
      headerName: "Ready For Review",
      type: "boolean",
      editable: true,
      renderCell: ({ value }) => (
        <span style={{ color: darkMode ? "white" : "black" }}>
          {value ? `✓` : "✕"}
        </span>
      ),
    },
    {
      field: "badgeColor",
      headerName: "Badge Color",
      type: "string",
      editable: true,
    },
    {
      field: "order",
      headerName: "Order",
      type: "number",
      editable: true,
    },
    {
      field: "createdDate",
      headerName: "Created Date",
      type: "string",
      editable: false,
      width: 150,
      valueFormatter: formatDate,
    },
    {
      field: "updatedDate",
      headerName: "Updated Date",
      type: "dateTime",
      editable: false,
      width: 150,
      valueFormatter: formatDate,
    },
    {
      field: "tenantId",
      headerName: "TenantId",
      type: "string",
      editable: false,
    },
    {
      field: "authorId",
      headerName: "AuthorId",
      type: "string",
      editable: false,
    },
    {
      field: "modifiedById",
      headerName: "ModifiedById",
      type: "string",
      editable: false,
    },
  ];

  const addItems = () => {
    setForm({
      ...form,
      title: "",
      badgeColor: '#8D97A0',
      order: 0,
      id: "",
      isDefault: false,
      isCompleted: false,
      isReadOnly: false,
      isReadyForReview: false,
    });
    setModal({ ...modal, add: true });
    setErrors({});
  };

  const handleError = (error: unknown) => {
    if (error instanceof Error) {
      console.log(error.message);
      ToastMessage.Failed(error.message);
    } else {
      console.log("An unknown error occurred");
      ToastMessage.Failed();
    }
  };

  const handleSave = async () => {
    const isValid = validateSchema(statusSchema, { title: form.title });

    setErrors(isValid.errors);
    if (!isValid.result) return;

    const upForm: StatusInterface = {
      id: form.id,
      title: form.title,
      badgeColor: form.badgeColor,
      order: form.order,
      isDefault: form.isDefault,
      isCompleted: form.isCompleted,
      isReadOnly: form.isReadOnly,
      isReadyForReview: form.isReadyForReview
    };

    console.log('before Form', upForm);
    if (upForm.id === "") {
      const { id, ...formData } = upForm;
      addStatus.mutate(formData, {
        onSuccess: () => {
          refetch();
          ToastMessage.Success();
        },
        onError: (error) => handleError(error),
      });
    } else {
      updateStatus.mutate(upForm, {
        onSuccess: (response) => {
          refetch();
          ToastMessage.Success();
          console.log('response', response);
        },
        onError: (error) => handleError(error),
      });
    }

    setModal({ ...modal, add: false, edit: false, view: false });
  };

  const handleDelete = async (id: string) => {
    if (await ToastMessage.Confirm(language["ToastDeleteItem"])) {
      deleteStatus.mutate(id, {
        onSuccess: () => ToastMessage.Success(),
        onError: (error) => handleError(error),
      });
    }
  };

  return (
    <CustomDataGrid
      name={language["Statuses"]}
      rows={statuses || []}
      hideColumns={["authorId", "id", "tenantId", "modifiedById"]}
      showColumnModal={[
        "title",
        "badgeColor",
        "order",
        "isDefault",
        "isCompleted",
        "isReadOnly",
        "isReadyForReview",
      ]}
      form={form}
      page={page}
      modal={modal}
      errors={errors}
      columns={columns}
      loading={isLoading}
      pageSize={pageSize}
      setPage={setPage}
      setForm={setForm}
      setModal={setModal}
      addItems={addItems}
      setErrors={setErrors}
      handleSave={handleSave}
      setPageSize={setPageSize}
      handleDelete={handleDelete}
    />
  );
};
export default Statuses;
